import {Form, Formik, FormikProps} from 'formik';
import {get, isEmpty} from 'lodash';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Accordion, AccordionDetails, AccordionTitle, Alert, Button, DataWrapper,
    MultiInputFormik, UnsavedChangesLeavePrompt
} from 'components';
import {Option} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';

import {LiveHelpMessageInput} from '../LiveHelpMessageInput';
import {FIELDS} from '../constants';
import {LiveHelpMessagesFields, MessageItem, MessageType} from '../types';
import {liveHelpUtils} from '../utils';

type LiveHelpWelcomeProps = {
    loading: boolean;
    initialValues: LiveHelpMessagesFields;
    interactiveChatOptions: Option[];
    messageTypeOptions: Option<MessageType>[];
    onSubmit: (values: LiveHelpMessagesFields) => void;
};

type FormTemplateProps = {
    loading: boolean;
    initialValues: LiveHelpMessagesFields;
    messageTypeOptions: Option<MessageType>[];
    interactiveChatOptions: Option[];
} & FormikProps<LiveHelpMessagesFields>;

const T_KEYS_LIVE_HELP = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.LIVE_HELP;
const T_KEYS = T_KEYS_LIVE_HELP.AVATAR_WELCOME;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

const FormTemplate: React.FC<FormTemplateProps> = ({
    loading, initialValues, submitForm, errors, messageTypeOptions, interactiveChatOptions, values
}) => {
    const {t} = useTranslation();

    const validateItem = useCallback((item: MessageItem) => liveHelpUtils.validateItem(item, values), [values]);

    const hasNoChanges = useMemo(() => liveHelpUtils.hasNoChanges(values, initialValues), [initialValues, values]);
    const interactiveChatPositionWarning = useMemo(() => get(errors?.messages, 'interactiveChatPosition'), [errors]);

    return (
        <Form className='tw-relative'>
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                <Accordion className='tw-mt-2' defaultExpanded>
                    <AccordionTitle>
                        <div className='tw-flex tw-justify-between tw-font-bold tw-text-lg'>
                            {t(T_KEYS.TITLE)}
                        </div>
                    </AccordionTitle>
                    <AccordionDetails>
                        <div className='tw-text-default tw-my-4'>
                            {t(T_KEYS.DESCRIPTION)}
                        </div>
                        <MultiInputFormik
                            name={FIELDS.MESSAGES}
                            itemsPath={FIELDS.MESSAGES}
                            values={values}
                            maxItems={1}
                            defaultValue={{type: MessageType.InteractiveChat, text: ''}}
                            isValid={validateItem}
                        >
                            {({index, item, path, last, renderActions}) => (
                                <div key={index} className='tw-flex tw-full tw-items-center tw-mb-4'>
                                    <LiveHelpMessageInput
                                        item={item}
                                        path={path}
                                        last={last}
                                        interactiveChatOptions={interactiveChatOptions}
                                        messageTypeOptions={messageTypeOptions}
                                    />
                                    <div className='tw-w-10'>{renderActions({index, item})}</div>
                                </div>
                            )}
                        </MultiInputFormik>

                        {interactiveChatPositionWarning &&
                            <Alert type='warning' className='tw-my-4'>{t(T_KEYS_LIVE_HELP.INTERACTIVE_CHAT_ITEM_NOT_LAST_WARNING)}</Alert>}
                        <div className='tw-text-right tw-my-4'>
                            <Button
                                color='primary'
                                disabled={hasNoChanges || !isEmpty(errors)}
                                loading={loading}
                                onClick={submitForm}
                            >
                                {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                            </Button>
                        </div>
                    </AccordionDetails>
                </Accordion>
            </DataWrapper>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

export const LiveHelpAvatarWelcome: React.FC<LiveHelpWelcomeProps> = ({
    loading, messageTypeOptions, interactiveChatOptions, initialValues, onSubmit
}) => {
    const {t} = useTranslation();

    const validate = useCallback(({messages}: LiveHelpMessagesFields) => liveHelpUtils.validateMessages(messages, t), [t]);

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            validate={validate}
            validateOnChange
            validateOnBlur
            onSubmit={onSubmit}
        >
            {(props) => (
                <FormTemplate
                    {...props}
                    loading={loading}
                    interactiveChatOptions={interactiveChatOptions}
                    messageTypeOptions={messageTypeOptions}
                />
            )}
        </Formik>
    );
};